import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, MapPin, Crosshair, Pencil } from "lucide-react";
import { toast } from "sonner";

// Parse "lat, lon" decimal-degrees. Copied from ProjectCreatePage so
// this dialog stays self-contained — it's the same 2-line parser and
// duplicating it beats hoisting into a shared util that only two
// components would use.
function parseCoords(raw) {
  const s = (raw || "").trim();
  if (!s) return { lat: null, lng: null, error: "" };
  const cleaned = s.replace(/[°]/g, "").replace(/,\s*/, " ");
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length !== 2) return { lat: null, lng: null, error: "Enter two numbers: latitude, longitude." };
  const lat = Number(parts[0]);
  const lng = Number(parts[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return { lat: null, lng: null, error: "Coordinates must be numbers." };
  if (lat < -90 || lat > 90) return { lat: null, lng: null, error: "Latitude must be between -90 and 90." };
  if (lng < -180 || lng > 180) return { lat: null, lng: null, error: "Longitude must be between -180 and 180." };
  return { lat, lng, error: "" };
}

/**
 * Admin/PM-only inline edit for a project's location. Opens a dialog
 * pre-populated with whichever fields the project currently has, and
 * saves via PATCH /projects/{id}. Coordinates are validated the same
 * way as the Create form.
 *
 * Rendered as a small pencil icon on the LocationRow; the whole row
 * becomes clickable so users on mobile don't have to hit a tiny icon.
 */
export default function EditLocationButton({ project, canEdit, onSaved }) {
  const [open, setOpen] = useState(false);
  const [coordsInput, setCoordsInput] = useState(() => {
    if (typeof project?.latitude === "number" && typeof project?.longitude === "number") {
      return `${project.latitude}, ${project.longitude}`;
    }
    return "";
  });
  const [address, setAddress] = useState(project?.address || "");
  const [busy, setBusy] = useState(false);

  const parsed = parseCoords(coordsInput);
  const canSave = parsed.lat !== null && !busy;

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    try {
      const r = await api.patch(`/projects/${project.id}`, {
        latitude: parsed.lat,
        longitude: parsed.lng,
        address: address.trim(),
      });
      toast.success("Location updated");
      setOpen(false);
      onSaved?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update location");
    } finally {
      setBusy(false);
    }
  };

  if (!canEdit) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-blue-300 transition-colors"
        title="Edit location"
        data-testid="edit-location-btn"
      >
        <Pencil className="w-3 h-3" />
        edit
      </button>

      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent
          className="bg-slate-900 border-slate-800 text-slate-100"
          data-testid="edit-location-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display">Edit Project Location</DialogTitle>
            <DialogDescription className="text-slate-400">
              GPS coordinates drive the fleet map + the Navigate button. Address is
              optional supplemental info.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2 space-y-4">
            <div>
              <Label className="text-[11px] tracking-widest uppercase text-slate-400 font-display font-bold">
                GPS Coordinates <span className="text-red-400">*</span>
              </Label>
              <Input
                value={coordsInput}
                onChange={(e) => setCoordsInput(e.target.value)}
                placeholder="35.123456, -97.654321"
                className="mt-1.5 bg-slate-950 border-slate-700 font-mono"
                inputMode="decimal"
                data-testid="edit-location-coords"
              />
              <div className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1">
                <Crosshair className="w-3 h-3" /> Decimal degrees — latitude first, then longitude.
              </div>
              {parsed.error && (
                <div className="text-[11px] text-red-400 mt-1" data-testid="edit-location-coords-error">
                  {parsed.error}
                </div>
              )}
              {!parsed.error && parsed.lat !== null && (
                <div className="text-[11px] text-emerald-400 mt-1 font-mono">
                  Parsed: lat {parsed.lat}, lon {parsed.lng}
                </div>
              )}
            </div>

            <div>
              <Label className="text-[11px] tracking-widest uppercase text-slate-400 font-display font-bold">
                Address <span className="text-slate-500 font-normal normal-case tracking-normal ml-1">(optional)</span>
              </Label>
              <Textarea
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="123 Main St, City, State ZIP"
                className="mt-1.5 bg-slate-950 border-slate-700"
                rows={2}
                data-testid="edit-location-address"
              />
              <div className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Human-readable label — the Navigate button always uses the coords above.
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={busy}
              data-testid="edit-location-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={!canSave}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="edit-location-save"
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
